import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { TimbaPrediction } from '../api/client';
import { formatPick } from '../lib/timba';

// Popup que explica cómo se llegó a los puntos de un partido: marcador × booster + bonus de llave.
// Se monta en document.body (portal) para escapar de la card clickeable.
export function PointsBreakdown({ pred, t1, t2, onClose }: { pred: TimbaPrediction; t1?: string | null; t2?: string | null; onClose: () => void }) {
  const base = pred.base ?? pred.points ?? 0;
  const booster = pred.booster ?? 1;
  const bonus = pred.bonus ?? 0;
  const total = pred.points ?? base;

  return createPortal(
    <Backdrop onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <h3>Tus puntos en este partido</h3>
        <p className="pick">Tu pronóstico: <b>{formatPick(pred, t1, t2)}</b></p>
        <ul>
          <li>
            <span>⚽ Marcador</span>
            <span><b>{base}</b></span>
          </li>
          {booster > 1 && (
            <li className="boost">
              <span>🔥 Booster ×{booster}</span>
              <span><b>{base} → {base * booster}</b></span>
            </li>
          )}
          {bonus > 0 && (
            <li className="llave">
              <span>🔑 Bonus de llave</span>
              <span><b>+{bonus}</b></span>
            </li>
          )}
        </ul>
        <div className="total">
          <span>Total</span>
          <span><b>{total}</b> {total === 1 ? 'punto' : 'puntos'}</span>
        </div>
        <button type="button" onClick={onClose}>Cerrar</button>
      </Panel>
    </Backdrop>,
    document.body,
  );
}

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const Panel = styled.div`
  width: 100%;
  max-width: 340px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: 16px;
  padding: 18px;

  h3 { margin: 0 0 4px; font-size: 1.1rem; }
  .pick { margin: 0 0 12px; font-size: 0.84rem; color: ${({ theme }) => theme.colors.textMuted}; }
  .pick b { color: ${({ theme }) => theme.colors.text}; }

  ul { list-style: none; margin: 0; padding: 0; }
  li {
    display: flex; justify-content: space-between; align-items: center;
    padding: 9px 0; font-size: 0.88rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    color: ${({ theme }) => theme.colors.textMuted};
  }
  li b { color: ${({ theme }) => theme.colors.text}; }
  li.boost b { color: ${({ theme }) => theme.colors.boost}; }
  li.llave b { color: ${({ theme }) => theme.colors.gold}; }

  .total {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 12px; padding: 12px; border-radius: 12px;
    background: ${({ theme }) => theme.colors.surfaceAlt}; font-weight: 700;
  }
  .total b { font-size: 1.4rem; color: ${({ theme }) => theme.colors.accent}; }

  button {
    width: 100%; margin-top: 16px; padding: 12px;
    background: ${({ theme }) => theme.colors.accent}; color: ${({ theme }) => theme.colors.accentText};
    border: none; border-radius: 999px; font-weight: 800; font-size: 0.95rem;
  }
`;
